
import { defineComponent, SetupContext, ref, ComponentPublicInstance, onMounted, computed } from "vue";
import NativeClient from "@/client/native-client";

export default defineComponent({
  name: "FridgeDetailsDrumPicker",
  props: {
    /** 分の下限 */
    minutesLowerLimit: {
      type: Number,
      required: true
    },
    /** 分の上限 */
    minutesUpperLimit: {
      type: Number,
      required: true
    },
    /** 分の初期値 */
    initialMinutes: {
      type: Number,
      required: true
    },
    /** 秒有効 */
    isValidSeconds: {
      type: Boolean,
      required: true
    },
    /** 秒の下限 */
    secondsLowerLimit: {
      type: Number,
      default: 0
    },
    /** 秒の上限 */
    secondsUpperLimit: {
      type: Number,
      default: 0
    },
    /** 分の初期値 */
    initialSeconds: {
      type: Number,
      default: 0
    }
  },
  setup(props, context: SetupContext) {
    interface HTMLElementEvent<T extends HTMLElement> extends Event {
      target: T;
    }
    type HTMLDivElementEvent = HTMLElementEvent<HTMLDivElement>;

    // 定数
    const minutesText = "分";
    const secondsText = "秒";

    // 現在の分、秒
    let currentMinutes = 0;
    let currentSeconds = 0;
    const sendCurrentTime = () => {
      NativeClient.sendDebugLog("FridgeDetailsDrumPicker sendCurrentTime currentMinutes = " + currentMinutes + ", currentSeconds = " + currentSeconds);
      context.emit("select-time", currentMinutes, currentSeconds);
    };

    // コンテンツ生成
    interface FridgeDetailsDrumPickerContents {
      index: number;
      value: number;
      displayText: string;
    }
    const minutesContents = computed(() => {
      const result: Array<FridgeDetailsDrumPickerContents> = [];
      let index = 0;
      for (let value = props.minutesLowerLimit; value <= props.minutesUpperLimit; value ++) {
        const element: FridgeDetailsDrumPickerContents = { index: index, value: value, displayText: String(value) };
        result.push(element);
        index ++;
      }
      return result;
    });
    const secondsContents = computed(() => {
      const result: Array<FridgeDetailsDrumPickerContents> = [];
      let index = 0;
      // 00〜50を5セット生成する
      const startValue = 0;
      const endValue = 290;
      for (let value = startValue; value <= endValue; value += 10) {
        const displayValue = value % 60;
        const element: FridgeDetailsDrumPickerContents = { index: index, value: displayValue, displayText: ('00' + displayValue).slice(-2) };
        result.push(element);
        index ++;
      }
      NativeClient.sendDebugLog("FridgeDetailsDrumPicker secondsContents result = " + JSON.stringify(result));
      return result;
    });

    // 指定した位置にsクロールポジションを移動
    const setDrumScrollTo = (scrollElement: ComponentPublicInstance<HTMLInputElement>, contentElement: ComponentPublicInstance<HTMLElement>, position: number, isSmooth: boolean) => {
      scrollElement.style.pointerEvents = 'none';
      //NativeClient.sendDebugLog("FridgeDetailsDrumPicker setDrumScrollTo position = " + position);
      const elements = contentElement.children;
      const items = Array.from(elements);
      if (isSmooth) {
        scrollElement.scrollTo({ top: (items[position].clientHeight * position), left: 0, behavior: 'smooth' });
      } else {
        scrollElement.scrollTo({ top: (items[position].clientHeight * position), left: 0 });
      }

      // スクロールの有効化
      if (isSmooth) {
        window.setTimeout(() => {
          scrollElement.style.pointerEvents = 'auto';
          //NativeClient.sendDebugLog("FridgeDetailsDrumPicker setDrumScrollTo end smooth");
        }, 100);
      } else {
        scrollElement.style.pointerEvents = 'auto';
        //NativeClient.sendDebugLog("FridgeDetailsDrumPicker setDrumScrollTo end not smooth");
      }
      //NativeClient.sendDebugLog("FridgeDetailsDrumPicker setDrumScrollTo end position = " + position);
    };
    const findExactElementIndex = (scrollElement: ComponentPublicInstance<HTMLInputElement>, contentElement: ComponentPublicInstance<HTMLElement>) => {
      const scrollRect = scrollElement.getBoundingClientRect();
      const scrollVerticalCenter = scrollRect.top + scrollRect.height / 2;
      const elements = contentElement.children;
      const items = Array.from(elements);
      for (let index = 0; index < items.length; index ++) {
        const element = items[index];
        const clientRect = element.getBoundingClientRect();
        if (clientRect.top < scrollVerticalCenter && scrollVerticalCenter <= clientRect.bottom) {
          return index;
        }
      }
      return null;
    };

    // 秒スクロール
    let secondsStartTopPosition = 0;
    let secondsTouchStartY = 0;
    let secondsMoveY = 0;
    let isSecondsDrumTouching = false;
    const secondsScroll = ref<ComponentPublicInstance<HTMLInputElement>>();
    const secondsParentContent = ref<ComponentPublicInstance<HTMLElement>>();
    const secondsTouchStart = (e: TouchEvent) => {
      e.stopPropagation();
      isSecondsDrumTouching = true;
      secondsTouchStartY = e.touches[0].pageY;
      if (secondsScroll.value != undefined) {
        secondsStartTopPosition = secondsScroll.value.scrollTop;
      }
    };
    const secondsTouchMove = (e: TouchEvent) => {
      e.stopPropagation();
      secondsMoveY = e.touches[0].pageY - secondsTouchStartY;
      const topPosition = secondsStartTopPosition - secondsMoveY;
    };
    const secondsTouchEnd = (e: TouchEvent) => {
      e.stopPropagation();
      isSecondsDrumTouching = false;
    };
    let secondsTimerId: number | null = null;
    const setSecondsDrumScrollTo = (position: number, isSmooth: boolean) => {
      if (secondsScroll.value != undefined && secondsParentContent.value != undefined) {
        setDrumScrollTo(secondsScroll.value, secondsParentContent.value, position, isSmooth);
      }
    };
    let lastCenterIndex = -1;
    const onSecondsScrollEnd = () => {
      secondsTimerId = null;
      if (isSecondsDrumTouching) {
        return;
      }
      //NativeClient.sendDebugLog("FridgeDetailsDrumPicker onSecondsScrollEnd start");
      // 常に停止する位置は、scroll-snapにより、項目の切れ目になる
      if (secondsScroll.value != undefined && secondsParentContent.value != undefined) {
        const index = findExactElementIndex(secondsScroll.value, secondsParentContent.value);
        NativeClient.sendDebugLog("FridgeDetailsDrumPicker onSecondsScrollEnd index = " + index);
        if (index != null) {
          // 上限、加減をチェック
          const tempSeconds = index * 10 % 60;
          NativeClient.sendDebugLog("FridgeDetailsDrumPicker onSecondsScrollEnd tempSeconds = " + tempSeconds);
          let centerIndex = 0;
          let isAdjust = false;
          if (currentMinutes == props.minutesUpperLimit && props.secondsUpperLimit < tempSeconds) {
            // 上限値をオーバーにより、当該セットの上限値に移動
            currentSeconds = props.secondsUpperLimit;
            centerIndex = Math.floor(index / 6) * 6 + Math.floor(props.secondsUpperLimit / 10) + 6;
            isAdjust = true;
            NativeClient.sendDebugLog("FridgeDetailsDrumPicker onSecondsScrollEnd over upper limit = " + props.secondsUpperLimit + ", centerIndex = " + centerIndex + ", index = " + index);
          } else if (currentMinutes == props.minutesLowerLimit && tempSeconds < props.secondsLowerLimit) {
            // 下限を下回っているため、当該セットの下限値に移動
            currentSeconds = props.secondsLowerLimit;
            centerIndex = Math.floor(index / 6) * 6 + Math.floor(props.secondsLowerLimit / 10);
            isAdjust = true;
            NativeClient.sendDebugLog("FridgeDetailsDrumPicker onSecondsScrollEnd over lower limit = " + props.secondsLowerLimit + ", centerIndex = " + centerIndex);
          } else {
            // 第３セットに移動
            currentSeconds = index * 10 % 60;
            centerIndex = currentSeconds / 10 + 12;
          }
          const topIndex = centerIndex - 1;
          NativeClient.sendDebugLog("FridgeDetailsDrumPicker onSecondsScrollEnd topIndex = " + topIndex);
          if (isAdjust) {
            NativeClient.sendDebugLog("FridgeDetailsDrumPicker onSecondsScrollEnd adjust");
            // 以下の中でタッチ禁止を解除
            setSecondsDrumScrollTo(topIndex, true);
            sendCurrentTime();
          } else if (index != centerIndex) {
            NativeClient.sendDebugLog("FridgeDetailsDrumPicker onSecondsScrollEnd adjust third set centerIndex = " + centerIndex);
            // ちらつきを抑えるため、overflowYをhiddenに設定する（iOS対策）
            secondsScroll.value!.style.overflowY = 'hidden';
            setSecondsDrumScrollTo(topIndex, false);
            lastCenterIndex = centerIndex;
            secondsScroll.value!.style.overflowY = 'auto';
            sendCurrentTime();
          } else {
            NativeClient.sendDebugLog("FridgeDetailsDrumPicker onSecondsScrollEnd no adjust");
            sendCurrentTime();
          }
        }
      }
    };
    const onSecondsScroll = (event: HTMLDivElementEvent) => {
      //NativeClient.sendDebugLog("FridgeDetailsDrumPicker onSecondsScroll scrollTop = " + event.target.scrollTop);
      if (secondsTimerId != null) {
        NativeClient.sendDebugLog("FridgeDetailsDrumPicker onSecondsScroll clear timeout");
        clearTimeout(secondsTimerId);
      }
      if (secondsScroll.value != undefined && secondsParentContent.value != undefined) {
        secondsTimerId = window.setTimeout(() => {
          NativeClient.sendDebugLog("FridgeDetailsDrumPicker onSecondsScroll call onSecondsScrollEnd");
          onSecondsScrollEnd();
        }, 50);
      }
    };

    // 分スクロール
    let minutesStartTopPosition = 0;
    let minutesTouchStartY = 0;
    let minutesMoveY = 0;
    const minutesScroll = ref<ComponentPublicInstance<HTMLInputElement>>();
    const minutesParentContent = ref<ComponentPublicInstance<HTMLElement>>();
    let minutesTimerId: number | null = null;
    let isMinutesTouching = false;
    let isMinutesScrolling = false;
    const setMinutesDrumScrollTo = (position: number, isSmooth: boolean) => {
      //NativeClient.sendDebugLog("FridgeDetailsDrumPicker setMinutesDrumScrollTo scrollHeight = " + minutesScroll.value?.scrollHeight);
      if (minutesScroll.value != undefined && minutesParentContent.value != undefined) {
        setDrumScrollTo(minutesScroll.value, minutesParentContent.value, position, isSmooth);
      }
    };
    const onMinutesScrollEnd = () => {
      minutesTimerId = null;
      isMinutesScrolling = false;
      NativeClient.sendDebugLog("FridgeDetailsDrumPicker onMinutesScrollEnd start");
      if (minutesScroll.value != undefined && minutesParentContent.value != undefined) {
        // スクロール領域の情報取得
        const index = findExactElementIndex(minutesScroll.value, minutesParentContent.value);
        NativeClient.sendDebugLog("FridgeDetailsDrumPicker onMinutesScrollEnd index = " + index);
        if (index != null) {
          currentMinutes = index + props.minutesLowerLimit;
          // 上限、加減をチェックする
          if (currentMinutes == props.minutesUpperLimit && props.secondsUpperLimit < currentSeconds) {
            // 上限値を超えている
            NativeClient.sendDebugLog("FridgeDetailsDrumPicker onMinutesScrollEnd upper");
            currentSeconds = props.secondsUpperLimit;
            const position = Math.floor(currentSeconds / 10) + 11;
            setDrumScrollTo(secondsScroll.value!, secondsParentContent.value!, position, true);
          } else if (currentMinutes == props.minutesLowerLimit && currentSeconds < props.secondsLowerLimit) {
            // 下限値を下回っている
            NativeClient.sendDebugLog("FridgeDetailsDrumPicker onMinutesScrollEnd lower");
            currentSeconds = props.secondsLowerLimit;
            const position = Math.floor(currentSeconds / 10) + 11;
            NativeClient.sendDebugLog("FridgeDetailsDrumPicker onMinutesScrollEnd lower currentSeconds = " + currentSeconds);
            setDrumScrollTo(secondsScroll.value!, secondsParentContent.value!, position, true);
          }
          sendCurrentTime();
        }
      }
    };
    const onMinutesScroll = (event: HTMLDivElementEvent) => {
      //NativeClient.sendDebugLog("FridgeDetailsDrumPicker onMinutesScroll scrollTop = " + event.target.scrollTop);
      isMinutesScrolling = true;
      if (minutesTimerId != null) {
        //NativeClient.sendDebugLog("FridgeDetailsDrumPicker onMinutesScroll clear timeout");
        clearTimeout(minutesTimerId);
        minutesTimerId = null;
      }
      minutesTimerId = window.setTimeout(onMinutesScrollEnd, 100);
    };
    const minitesTouchStart = (e: TouchEvent) => {
      isMinutesTouching = true;
      e.stopPropagation();
      minutesTouchStartY = e.touches[0].pageY;
      if (minutesScroll.value != undefined) {
        minutesStartTopPosition = minutesScroll.value.scrollTop;
      }
    };
    const minitesTouchMove = (e: TouchEvent) => {
      e.stopPropagation();
      minutesMoveY = e.touches[0].pageY - minutesTouchStartY;
      const topPosition = minutesStartTopPosition - minutesMoveY;
      NativeClient.sendDebugLog("FridgeDetailsDrumPicker minitesTouchMove topPosition = " + topPosition);
      NativeClient.sendDebugLog("FridgeDetailsDrumPicker minitesTouchMove x = " + e.touches[0].pageX + ", y = " + e.touches[0].pageY);
      const rect = minutesScroll.value!.getClientRects();
      NativeClient.sendDebugLog("FridgeDetailsDrumPicker minitesTouchMove minutesScroll rect = " + JSON.stringify(rect));
      if (e.touches[0].pageY < rect[0].top || rect[0].bottom < e.touches[0].pageY) {
        NativeClient.sendDebugLog("FridgeDetailsDrumPicker minitesTouchMove overboundary ");
        const e = new TouchEvent("touchend");
        minutesScroll.value!.dispatchEvent(e);
      }
    };
    const minitesTouchEnd = (e: TouchEvent) => {
      NativeClient.sendDebugLog("FridgeDetailsDrumPicker minitesTouchEnd");
      isMinutesTouching = false;
      e.stopPropagation();
      minutesMoveY = 0;
      minutesTouchStartY = 0;
    };

    // 表示された場合
    onMounted(() => {
      const initialPosition = props.initialMinutes - props.minutesLowerLimit;
      NativeClient.sendDebugLog("FridgeDetailsDrumPicker onMounted initialMinutes = " + props.initialMinutes + ", initialPosition = " + initialPosition);
      currentMinutes = props.initialMinutes;
      // iOS14.4.x以前では、初期設定時に0位置に矯正スクロールさせると、スクロールできなくなるため、0位置はスクロールさせない
      if (initialPosition > 0) {
        NativeClient.sendDebugLog("FridgeDetailsDrumPicker onMounted initialPosition = " + initialPosition);
        setMinutesDrumScrollTo(initialPosition, false);
      // } else {
      //   setMinutesDrumScrollTo(0, false);
      }

      // 秒有効な場合
      if (props.isValidSeconds) {
        NativeClient.sendDebugLog("FridgeDetailsDrumPicker onMounted initialSeconds = " + props.initialSeconds);
        currentSeconds = props.initialSeconds;
        const initialPosition = props.initialSeconds / 10 + 11;
        setSecondsDrumScrollTo(initialPosition, false);
      }
    });

    return {
      minutesContents,
      minutesText,
      minutesScroll,
      onMinutesScroll,
      minitesTouchStart,
      minitesTouchMove,
      minitesTouchEnd,
      minutesParentContent,
      secondsContents,
      secondsText,
      secondsScroll,
      onSecondsScroll,
      secondsTouchStart,
      secondsTouchMove,
      secondsTouchEnd,
      secondsParentContent
    };
  }
});
